import React, { useState, useEffect, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EurosillKonfigurator() {
  // State Management
  const [currentStep, setCurrentStep] = useState(0);
  const [kommission, setKommission] = useState('');
  
  // Lagerlängen State - VPE-basiert (kein Längen-Input)
  const [currentBank, setCurrentBank] = useState({
    ausladung: 50,  
    farbe: 'RAL9016 weiß',
    ralCustom: '',
    anzahl_vpe: 1, // Direkte VPE-Eingabe (10 Stäbe à 6m pro VPE)
    bordprofil: 'GAA320',
    bordprofil_links_vpe: 0, // VPE für Bordprofil links (30 Stück pro VPE)
    bordprofil_rechts_vpe: 0, // VPE für Bordprofil rechts (30 Stück pro VPE)
    anzahl: 1 // Anzahl Fensterbänke
  });
  
  // Schritte definieren
  const steps = [
    { id: 1, name: "Standard Fensterbank", description: "Produkte konfigurieren" },
    { id: 2, name: "Konfiguration prüfen", description: "Bestellliste überprüfen" },
    { id: 3, name: "Kundendaten", description: "Kontaktdaten eingeben" },
    { id: 4, name: "Bestätigung", description: "Bestellung abschließen" }
  ];

  // Warenkorb - Liste aller Fensterbänke
  const [warenkorb, setWarenkorb] = useState([]);
  const [produktListe, setProduktListe] = useState([]); // Neue Produktliste für VPE-System
  
  // Konfigurationsdaten aus Backend
  const [configData, setConfigData] = useState(null);
  const [ralData, setRalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Zusätzliche State für Zubehör
  const [zubehoer, setZubehoer] = useState({
    antidroehm: 'ja',
    antidroehm_breite: 'keine', // Neue Breiten-Auswahl
    antidroehm_vpe: 0, // VPE für Anti-Dröhn
    halter: 'keine', // Geändert von 'ohne Fensterbankhalter' zu 'keine'
    halter_groesse: '', // ISO Halter Größe 1-6 (korrekte Auszugsmaße)
    halter_vpe: 0, // VPE für Halter
    daemmstaerke: '',
    anschlussdichtung: 'keine', // Geändert von 'ja' zu 'keine'
    anschlussdichtung_vpe: 0, // VPE für Anschlussdichtung
    butylEcke: 'nein'  // Standardwert auf "nein" geändert
  });

  // Zubehör-States erweitert
  const [antidroehnstreifen_ja_nein, setAntidroehnstreifen_ja_nein] = useState('nein');
  const [antidroehn_modus, setAntidroehn_modus] = useState('lose'); // lose/aufgebracht
  // antidroehn_breite entfernt - wird automatisch bestimmt

  // Bordprofil Advanced Mode States
  const [bordprofilAdvanced, setBordprofilAdvanced] = useState(false);
  const [bordprofilLinks, setBordprofilLinks] = useState('GAA320');
  const [bordprofilRechts, setBordprofilRechts] = useState('GAA320');
  
  // Anti-Dröhn Breite wird automatisch aus Ausladung bestimmt - keine State-Variable mehr nötig
  const [anschlussdichtung_ja_nein, setAnschlussdichtung_ja_nein] = useState('nein');

  // Kundendaten State
  const [kundendaten, setKundendaten] = useState({
    unternehmen: '',
    ansprechpartner: '',
    email: '',
    strasse: '',
    plz: '',
    ort: '',
    haendler_optional: false,
    haendler_daten: ''  // Freitext-Eingabe für Name, Adresse, PLZ Ort
  });

  // Order Submission State
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [orderError, setOrderError] = useState(null);

  // Daten beim Laden abrufen
  useEffect(() => {
    loadConfigData();
    loadRalData();
  }, []);

  const loadConfigData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/config/base`);
      if (!response.ok) throw new Error('Fehler beim Laden der Konfigurationsdaten');
      const data = await response.json();
      setConfigData(data);
    } catch (err) {
      console.error('Config load error:', err);
      setError('Fehler beim Laden der Produktdaten: ' + err.message);
    }
  };

  const loadRalData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ral-datenbank`);
      if (!response.ok) throw new Error('Fehler beim Laden der RAL-Datenbank');
      const data = await response.json();
      
      // Convert array to object for easier lookup
      const ralLookup = {};
      data.forEach(ral => {
        ralLookup[ral.nummer] = ral.bezeichnung;
      });
      setRalData(ralLookup);
    } catch (err) {
      console.error('RAL load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // RAL-Nummer debounced lookup
  const [ralBezeichnung, setRalBezeichnung] = useState('');
  useEffect(() => {
    if (currentBank.ralCustom && currentBank.ralCustom.length >= 4) {
      const timer = setTimeout(() => {
        const bezeichnung = ralData[currentBank.ralCustom];
        setRalBezeichnung(bezeichnung || '');
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setRalBezeichnung('');
    }
  }, [currentBank.ralCustom, ralData]);

  // Hilfsfunktion für SM-Berechnung mit Segmentierung
  const calculateSMWithSegmentation = async (fm, bordprofil, oberflaeche) => {
    // Prüfen ob Segmentierung nötig (>3000mm)
    if (fm > 3000) {
      try {
        const stossResponse = await fetch(`${BACKEND_URL}/api/calculate/stossverbinder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fertigmass_mm: fm,
            oberflaeche_code: oberflaeche,
            bordprofil_code: bordprofil
          })
        });
        
        if (stossResponse.ok) {
          const stossResult = await stossResponse.json();
          if (stossResult.stossverbinder_noetig && stossResult.segmente_sm) {
            // Verwende segmentierte SM-Werte
            const segmenteSM = stossResult.segmente_sm;
            return `${stossResult.anzahl_segmente} Stück à ${segmenteSM[0]}mm`;
          }
        }
      } catch (error) {
        console.error('Fehler bei Stoßverbinder-Abfrage:', error);
      }
    }
    
    // Normale SM-Berechnung für <3000mm
    const sm = calculateSMfromFM(fm, bordprofil);
    return `SM: ${sm}mm`;
  };

  // Hilfsfunktion für SM-Berechnung basierend auf Bordprofil
  const calculateSMfromFM = (fm, bordprofil) => {
    // SM-Abzug basierend auf Bordprofil (aus Backend-Grundlage)
    const smAbzuege = {
      'AA100': 2,    // 2mm x 2 = 4mm
      'AA150': 2,    // 2mm x 2 = 4mm  
      'GAA320': 7.5, // 7.5mm x 2 = 15mm
      'GAA350': 7.5, // 7.5mm x 2 = 15mm
      'GA125P': 7.5, // 7.5mm x 2 = 15mm
      'GA125K': 7.5  // 7.5mm x 2 = 15mm
    };
    const abzug = smAbzuege[bordprofil] || 7.5; // Default GAA320
    return fm - (abzug * 2);
  };

  // Hilfsfunktion für Advanced Mode SM-Berechnung
  const calculateAdvancedSM = (item, bordprofilCode) => {
    // Advanced Mode: Unterschiedliche Bordprofile
    if (item.bordprofilLinks && item.bordprofilRechts && item.bordprofilLinks !== item.bordprofilRechts) {
      const smAbzuege = {
        'AA100': 2, 'AA150': 2, 'GAA320': 7.5, 'GAA350': 7.5, 'GA125P': 7.5, 'GA125K': 7.5
      };
      const abzugLinks = smAbzuege[item.bordprofilLinks] || 7.5;
      const abzugRechts = smAbzuege[item.bordprofilRechts] || 7.5;
      const sm = Math.floor(item.fertigmass - abzugLinks - abzugRechts + 0.5); // Rundung
      return sm;
    } else {
      return calculateSMfromFM(item.fertigmass, bordprofilCode);
    }
  };

  // Bestellung abschließen Handler - HIER erfolgt Weiterleitung zu Step 2
  const handleBestellungAbschließen = () => {
    console.log('handleBestellungAbschließen aufgerufen');
    console.log('Aktuelle Produktliste:', produktListe);
    
    if (produktListe.length === 0) {
      alert('Bitte fügen Sie mindestens ein Produkt zur Liste hinzu.');
      return;
    }
    
    // KRITISCH: Synchronisiere produktListe mit warenkorb vor dem Schritt-Wechsel
    // Dies ist notwendig, da handleSubmitOrder auf warenkorb prüft
    setWarenkorb([...produktListe]);
    console.log('Produktliste in Warenkorb kopiert:', produktListe);
    
    // Weiterleitung zu "Konfiguration prüfen"
    setCurrentStep(1);
    console.log('Wechsel zu Step 2: Konfiguration prüfen');
  };

  // Handler-Funktionen für "Zur Liste hinzufügen" - BLEIBEN auf Step 1
  const handleAddStangenware = () => {
    console.log('handleAddStangenware aufgerufen');
    if (currentBank.anzahl_vpe > 0) {
      const neuesProdukt = {
        id: Date.now() + Math.random(),
        typ: 'Stangenware',
        beschreibung: 'sill|tec40 BASE Alu-Außenfensterbank - Lagerlängen',
        ausladung: currentBank.ausladung,
        farbe: currentBank.farbe,
        ralCustom: currentBank.ralCustom,
        anzahl_vpe: currentBank.anzahl_vpe,
        anzahl_stäbe: currentBank.anzahl_vpe * 10,
        gesamtlaenge_m: currentBank.anzahl_vpe * 60,
        details: `${currentBank.anzahl_vpe} VPE = ${currentBank.anzahl_vpe * 10} Stäbe = ${currentBank.anzahl_vpe * 60}m`
      };
      
      console.log('Neues Produkt:', neuesProdukt);
      setProduktListe(prev => {
        const neue_liste = [...prev, neuesProdukt];
        console.log('Neue Produktliste:', neue_liste);
        return neue_liste;
      });
      
      // SYNCHRONISATION: Auch in warenkorb hinzufügen für handleSubmitOrder
      setWarenkorb(prev => [...prev, neuesProdukt]);
      
      // KEIN Seitenwechsel - bleibt auf Step 1 für weitere Erfassung
      console.log('Stangenware hinzugefügt - bleibe auf Step 1 für weitere Erfassung');
    } else {
      alert('Bitte VPE-Anzahl eingeben');
    }
  };

  const handleAddBordprofile = () => {
    const totalVPE = (currentBank.bordprofil_links_vpe || 0) + (currentBank.bordprofil_rechts_vpe || 0);
    if (totalVPE > 0) {
      const neuesProdukt = {
        id: Date.now() + Math.random(),
        typ: 'Bordprofile',
        beschreibung: configData?.bordprofile.find(bp => bp.code === currentBank.bordprofil)?.bezeichnung || currentBank.bordprofil,
        bordprofil: currentBank.bordprofil,
        ausladung: currentBank.ausladung, // Ausladung hinzufügen
        farbe: currentBank.farbe, // Farbe hinzufügen
        ralCustom: currentBank.ralCustom, // RAL Custom hinzufügen
        links_vpe: currentBank.bordprofil_links_vpe || 0,
        rechts_vpe: currentBank.bordprofil_rechts_vpe || 0,
        total_vpe: totalVPE,
        total_stück: totalVPE * 30,
        details: `Links: ${currentBank.bordprofil_links_vpe || 0} VPE, Rechts: ${currentBank.bordprofil_rechts_vpe || 0} VPE = ${totalVPE * 30} Stück`
      };
      
      setProduktListe(prev => [...prev, neuesProdukt]);
      // SYNCHRONISATION: Auch in warenkorb hinzufügen
      setWarenkorb(prev => [...prev, neuesProdukt]);
      console.log('Bordprofile hinzugefügt:', neuesProdukt);
      
      // KEIN Seitenwechsel - bleibt auf Step 1 für weitere Erfassung
      console.log('Bordprofile hinzugefügt - bleibe auf Step 1 für weitere Erfassung');
    } else {
      alert('Bitte VPE-Anzahl für Links oder Rechts eingeben');
    }
  };

  const handleAddZubehör = () => {
    const zubehörItems = [];
    
    if (zubehoer.antidroehm_breite !== 'keine' && zubehoer.antidroehm_vpe > 0) {
      zubehörItems.push({
        id: Date.now() + Math.random() + 1,
        typ: 'Zubehör',
        beschreibung: `Anti-Dröhn-Streifen ${zubehoer.antidroehm_breite}mm`,
        breite: zubehoer.antidroehm_breite,
        anzahl_vpe: zubehoer.antidroehm_vpe,
        gesamt_meter: zubehoer.antidroehm_vpe * 100,
        details: `${zubehoer.antidroehm_vpe} VPE = ${zubehoer.antidroehm_vpe * 100}m`
      });
    }
    
    if (zubehoer.halter !== 'keine' && zubehoer.halter_vpe > 0) {
      const vpeSize = zubehoer.halter === 'Halter|ISO' ? 10 : 50;
      const größeText = zubehoer.halter === 'Halter|ISO' && zubehoer.halter_groesse ? 
        ` Größe ${zubehoer.halter_groesse}` : '';
      
      zubehörItems.push({
        id: Date.now() + Math.random() + 2,
        typ: 'Zubehör',
        beschreibung: `${zubehoer.halter}${größeText}`,
        anzahl_vpe: zubehoer.halter_vpe,
        gesamt_stück: zubehoer.halter_vpe * vpeSize,
        details: `${zubehoer.halter_vpe} VPE = ${zubehoer.halter_vpe * vpeSize} Stück`
      });
    }
    
    if (zubehoer.anschlussdichtung === 'ja' && zubehoer.anschlussdichtung_vpe > 0) {
      zubehörItems.push({
        id: Date.now() + Math.random() + 3,
        typ: 'Zubehör',
        beschreibung: 'Anschlussdichtung EPDM',
        anzahl_vpe: zubehoer.anschlussdichtung_vpe,
        gesamt_meter: zubehoer.anschlussdichtung_vpe * 50,
        details: `${zubehoer.anschlussdichtung_vpe} VPE = ${zubehoer.anschlussdichtung_vpe * 50}m`
      });
    }
    
    if (zubehörItems.length > 0) {
      setProduktListe(prev => [...prev, ...zubehörItems]);
      // SYNCHRONISATION: Auch in warenkorb hinzufügen  
      setWarenkorb(prev => [...prev, ...zubehörItems]);
      
      console.log('Zubehör hinzugefügt:', zubehörItems);
      
      // KEIN Seitenwechsel - bleibt auf Step 1 für weitere Erfassung
      console.log('Zubehör hinzugefügt - bleibe auf Step 1 für weitere Erfassung');
    } else {
      alert('Kein Zubehör mit VPE-Anzahl ausgewählt');
    }
  };

  // Lagerlängen-Berechnung (6m Stäbe, VPE)
  const calculateLagerlängen = async (gewuenschte_laenge_mm, ausladung, oberflaeche_code) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calculate/lagerlängen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gewünschte_laenge_mm: gewuenschte_laenge_mm,
          ausladung_mm: ausladung,
          oberflaeche_code: oberflaeche_code
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        return result;
      }
    } catch (error) {
      console.error('Fehler bei Lagerlängen-Berechnung:', error);
    }
    return null;
  };

  // Anti-Dröhn Breiten-Bestimmung basierend auf Ausladung
  const getAntiDroehnBreiten = (ausladung) => {
    const ausladungNum = parseInt(ausladung);
    if (ausladungNum >= 90 && ausladungNum <= 150) {
      return [50];
    } else if (ausladungNum >= 165 && ausladungNum <= 240) {
      return [80];
    } else if (ausladungNum >= 260 && ausladungNum <= 300) {
      return [100];
    } else if (ausladungNum >= 320 && ausladungNum <= 380) {
      return [80, 50]; // Kombination
    } else if (ausladungNum === 400) {
      return [100, 50]; // Kombination
    }
    return [80]; // Default fallback
  };

  // Info-Text für Anti-Dröhn Breite
  const getAntiDroehnBreiteInfo = (ausladung) => {
    const breiten = getAntiDroehnBreiten(ausladung);
    if (breiten.length === 1) {
      return `${breiten[0]}mm (auto)`;
    } else {
      return `Kombination: ${breiten.join('+')}mm (auto)`;
    }
  };

  // Funktion zur Berechnung der kompletten Artikel-Liste
  const berechneArtikelListe = async () => {
    try {
      console.log('🚀 STARTE berechneArtikelListe - Warenkorb Größe:', warenkorb.length);
      setArtikelListe([]); // Reset
      const artikelMap = new Map(); // Für Zusammenfassung gleicher Artikel

      const addArtikel = (artikel) => {
        // Grundgesetz 7.x: Artikelkonsolidierung
        // Fensterbänke (1020...) und Balkonaustritte (1041...) werden NIEMALS zusammengefasst
        if (artikel.artikelnr.startsWith('1020') || artikel.artikelnr.startsWith('1041')) {
          // Jede Fensterbank bleibt separat (projektspezifische Länge und Etikettierung)
          const uniqueKey = `${artikel.artikelnr}_${artikel.fensterbank_id}_${Date.now()}_${Math.random()}`;
          artikelMap.set(uniqueKey, artikel);
        } else {
          // Zubehör, Verbinder, Halter: Bei identischer Artikelnummer addieren
          const key = artikel.artikelnr; // Nur Artikelnummer als Konsolidierungs-Key
          if (artikelMap.has(key)) {
            const existing = artikelMap.get(key);
            const oldMenge = existing.menge;
            existing.menge = parseFloat(existing.menge) + parseFloat(artikel.menge);
            console.log(`🔄 Konsolidierung ${key}: ${oldMenge} + ${artikel.menge} = ${existing.menge}`);
            // Sammle Fensterbank-Infos für Referenz
            existing.instances = existing.instances || [existing.fensterbank_info];
            if (artikel.fensterbank_info && !existing.instances.includes(artikel.fensterbank_info)) {
              existing.instances.push(artikel.fensterbank_info);
            }
          } else {
            artikelMap.set(key, {
              ...artikel,
              instances: [artikel.fensterbank_info]
            });
          }
        }
      };

      for (const item of warenkorb) {
        // RAL-Code aus Farbe extrahieren
        const getRalCode = (farbe, ralCustom) => {
          if (farbe === 'RAL bunt (RAL-Code Pflichtangabe)') {
            return ralCustom || '';
          }
          const match = farbe.match(/RAL(\d+)/);
          return match ? match[1] : '';
        };

        // Oberflächencode basierend auf RAL bestimmen
        const getOberflaeche = (ralCode) => {
          if (!ralCode) return '7';
          const ralMapping = {
            '9016': '3', // RAL9016 weiß
            '7016': '7', // RAL7016 anthrazitgrau  
            '8017': '6', // RAL bunt
            '6005': '6', // RAL bunt
            '5002': '6'  // RAL bunt
          };
          return ralMapping[ralCode] || '6';
        };

        const ralCode = getRalCode(item.farbe, item.ralCustom);
        const oberflaeche = getOberflaeche(ralCode);
        const bordprofilCode = item.bordprofil || 'GAA320';
        
        // SM-Berechnung mit Segmentierung
        let smInfo = '';
        if (item.fertigmass > 3000) {
          try {
            const stossResponse = await fetch(`${BACKEND_URL}/api/calculate/stossverbinder`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fertigmass_mm: item.fertigmass,
                oberflaeche_code: oberflaeche,
                bordprofil_code: bordprofilCode
              })
            });
            
            if (stossResponse.ok) {
              const stossResult = await stossResponse.json();
              if (stossResult.stossverbinder_noetig && stossResult.segmente_sm) {
                smInfo = `${stossResult.anzahl_segmente} Stück à ${stossResult.segmente_sm[0]}mm`;
              } else {
                smInfo = `SM: ${calculateAdvancedSM(item, bordprofilCode)}mm`;
              }
            } else {
              smInfo = `SM: ${calculateAdvancedSM(item, bordprofilCode)}mm`;
            }
          } catch (error) {
            smInfo = `SM: ${calculateAdvancedSM(item, bordprofilCode)}mm`;
          }
        } else {
          smInfo = `SM: ${calculateAdvancedSM(item, bordprofilCode)}mm`;
        }
        
        const fensterbank_info = `FM: ${item.fertigmass}mm, Ausladung: ${item.ausladung}mm, ${item.farbe}, ${bordprofilCode}, ${smInfo}`;
        
        // 1. Hauptprodukt - pro Anzahl
        const hauptartikel_nr = `1020${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
        
        // Beschreibung erweitern für Advanced Mode (unterschiedliche Bordprofile)
        let hauptprodukt_beschreibung = `Außenfensterbank sill|tec40 BASE`;
        const useAdvancedBordprofile = item.bordprofilLinks && item.bordprofilRechts && item.bordprofilLinks !== item.bordprofilRechts;
        
        if (useAdvancedBordprofile) {
          // Bordprofil-Bezeichnungen aus configData holen
          const bordprofilLinksName = configData?.bordprofile.find(bp => bp.code === item.bordprofilLinks)?.bezeichnung || item.bordprofilLinks;
          const bordprofilRechtsName = configData?.bordprofile.find(bp => bp.code === item.bordprofilRechts)?.bezeichnung || item.bordprofilRechts;
          hauptprodukt_beschreibung += ` (Sonderfall: Links ${bordprofilLinksName}, Rechts ${bordprofilRechtsName})`;
        }
        
        addArtikel({
          artikelnr: hauptartikel_nr,
          typ: 'hauptprodukt',
          beschreibung: hauptprodukt_beschreibung,
          menge: item.anzahl,
          einheit: 'Stk',
          fensterbank_id: item.id,
          fensterbank_info: fensterbank_info
        });

        // 2. Bordprofile - mit korrekter Ausladung in Artikelnummer, pro Anzahl
        // useAdvancedBordprofile bereits oben definiert
        
        if (useAdvancedBordprofile) {
          // Advanced Mode: Links und Rechts separat verarbeiten
          const processAdvancedBordprofil = (bordprofilCode, seite) => {
            let artikel_nr, bordprofil_name;
            
            if (bordprofilCode === 'AA100') {
              artikel_nr = seite === 'links' ? `1100${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1101${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'AA|100';
            } else if (bordprofilCode === 'AA150') {
              artikel_nr = seite === 'links' ? `1150${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1151${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'AA|150';
            } else if (bordprofilCode === 'GAA320') {
              artikel_nr = seite === 'links' ? `1320${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1321${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'GAA|320';
            } else if (bordprofilCode === 'GAA350') {
              artikel_nr = seite === 'links' ? `1350${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1351${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'GAA|350-P';
            } else if (bordprofilCode === 'GA125P') {
              artikel_nr = seite === 'links' ? `1125${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1126${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'GA|125-P';
            } else if (bordprofilCode === 'GA125K') {
              artikel_nr = seite === 'links' ? `1127${oberflaeche}${item.ausladung.toString().padStart(3, '0')}` : `1128${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
              bordprofil_name = 'GA|125-K';
            }
            
            if (artikel_nr) {
              addArtikel({
                artikelnr: artikel_nr,
                typ: 'bordprofil',
                beschreibung: `Bordprofil ${bordprofil_name} ${seite}`,
                menge: item.anzahl,
                einheit: 'Stk',
                fensterbank_id: item.id,
                fensterbank_info: fensterbank_info
              });
            }
          };
          
          // Links und Rechts verarbeiten
          processAdvancedBordprofil(item.bordprofilLinks, 'links');
          processAdvancedBordprofil(item.bordprofilRechts, 'rechts');
          
        } else {
          // Standard Mode: Gleiches Bordprofil links und rechts
          const bordprofil = bordprofilCode;
          let bordprofil_links_nr, bordprofil_rechts_nr, bordprofil_name;
        
        if (bordprofil === 'AA100') {
          bordprofil_links_nr = `1100${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1101${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'AA|100';
        } else if (bordprofil === 'AA150') {
          bordprofil_links_nr = `1150${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1151${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'AA|150';
        } else if (bordprofil === 'GAA320') {
          bordprofil_links_nr = `1320${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1321${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'GAA|320';
        } else if (bordprofil === 'GAA350') {
          bordprofil_links_nr = `1350${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1351${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'GAA|350-P';
        } else if (bordprofil === 'GA125P') {
          bordprofil_links_nr = `1125${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1126${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'GA|125-P';
        } else if (bordprofil === 'GA125K') {
          bordprofil_links_nr = `1127${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_rechts_nr = `1128${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
          bordprofil_name = 'GA|125-K';
        }

          if (bordprofil_links_nr) {
            addArtikel({
              artikelnr: bordprofil_links_nr,
              typ: 'bordprofil',
              beschreibung: `Bordprofil ${bordprofil_name} links`,
              menge: item.anzahl,
              einheit: 'Stk',
              fensterbank_id: item.id,
              fensterbank_info: fensterbank_info
            });
            
            addArtikel({
              artikelnr: bordprofil_rechts_nr,
              typ: 'bordprofil',
              beschreibung: `Bordprofil ${bordprofil_name} rechts`,
              menge: item.anzahl,
              einheit: 'Stk',
              fensterbank_id: item.id,
              fensterbank_info: fensterbank_info
            });
          }
        } // End of Standard/Advanced Mode

        // 3. Fensterbankhalter - pro Fensterbank einzeln berechnen, dann zusammenfassen
        if (item.halter && item.halter !== 'ohne Fensterbankhalter') {
          try {
            const halterPayload = {
              fertigmass_mm: item.fertigmass,
              halter_typ: item.halter,
              daemmstaerke_mm: parseInt(item.daemmstaerke) || 80  // Dämmstärke aus Frontend, Default 80mm
            };
            
            const halterResponse = await fetch(`${BACKEND_URL}/api/calculate/fensterbankhalter`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(halterPayload)
            });
            
            if (halterResponse.ok) {
              const halterResult = await halterResponse.json();
              if (halterResult.anzahl > 0) {
                // Dämmstärke nur bei ISO-Haltern hinzufügen
                let beschreibung = halterResult.bezeichnung;
                if (item.halter === 'Halter|ISO') {
                  beschreibung += ` (Dämmstärke: ${item.daemmstaerke || 80}mm)`;
                }
                
                addArtikel({
                  artikelnr: halterResult.artikelnr,
                  typ: 'fensterbankhalter',
                  beschreibung: beschreibung,
                  menge: halterResult.anzahl * item.anzahl, // Anzahl Halter × Anzahl Fensterbänke
                  einheit: 'Stk',
                  fensterbank_id: item.id,
                  fensterbank_info: fensterbank_info
                });
              }
            }
          } catch (error) {
            console.error('Fehler bei Halter-Berechnung:', error);
          }
        }

        // 4. Antidröhnstreifen - nur wenn aktiv
        if (item.antidroehm === 'ja') {
          try {
            // Automatische Breiten-Bestimmung basierend auf Ausladung
            const antiDroehnBreiten = getAntiDroehnBreiten(item.ausladung);
            console.log('🔍 ANTI-DRÖHN DEBUG für Fensterbank:', {
              fensterbank_id: item.id,
              fertigmass: item.fertigmass,
              ausladung: item.ausladung,
              automatische_breiten: antiDroehnBreiten,
              modus: item.antidroehm_modus || 'lose',
              anzahl: item.anzahl,
              warenkorb_total: warenkorb.length
            });
            
            // Für jede Breite einen separaten API-Call (bei Kombinationen)
            for (const breite of antiDroehnBreiten) {
              console.log(`🚀 API-Call für Breite: ${breite}mm, Modus: ${item.antidroehm_modus || 'lose'}, FM: ${item.fertigmass}mm`);
              
              const antidrohmResponse = await fetch(`${BACKEND_URL}/api/calculate/antidroehm-advanced`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  fertigmass_mm: item.fertigmass,
                  modus: item.antidroehm_modus || 'lose',
                  breite_mm: breite,
                  anzahl: item.anzahl
                })
              });
              
              if (antidrohmResponse.ok) {
                const antidrohmResult = await antidrohmResponse.json();
                console.log('✅ Antidröhnstreifen Ergebnis für Breite', breite + 'mm:', antidrohmResult);
                
                if (antidrohmResult.artikel_liste && antidrohmResult.artikel_liste.length > 0) {
                  antidrohmResult.artikel_liste.forEach(artikel => {
                    console.log('➕ ERGEBNIS: Füge Antidröhnstreifen hinzu:', {
                      artikelnr: artikel.artikelnr,
                      menge: artikel.menge,
                      einheit: artikel.einheit,
                      beschreibung: artikel.bezeichnung,
                      für_fensterbank: `${item.fertigmass}mm (ID: ${item.id})`,
                      breite_verwendet: `${breite}mm`
                    });
                    addArtikel({
                      artikelnr: artikel.artikelnr,
                      typ: 'antidroehm',
                      beschreibung: artikel.bezeichnung + (artikel.meta_gesamt ? ` (${artikel.meta_gesamt})` : '') + ` - ${breite}mm`,
                      menge: artikel.menge,
                      einheit: artikel.einheit || 'Stk',
                      fensterbank_id: item.id,
                      fensterbank_info: fensterbank_info
                    });
                  });
                } else {
                  console.log('⚠️ Keine Antidröhnstreifen-Artikel generiert für Breite', breite + 'mm');
                }
              } else {
                console.error('❌ Antidröhnstreifen API Fehler für Breite', breite + 'mm:', antidrohmResponse.status);
                
                // Fallback nur beim ersten Durchlauf (erste Breite)
                if (breite === antiDroehnBreiten[0]) {
                  const fallbackResponse = await fetch(`${BACKEND_URL}/api/calculate/antidroehm`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      fertigmass_mm: item.fertigmass,
                      antidroehm_aktiv: 'ja'
                    })
                  });
                  
                  if (fallbackResponse.ok) {
                    const fallbackResult = await fallbackResponse.json();
                    if (fallbackResult.anzahl > 0) {
                      addArtikel({
                        artikelnr: fallbackResult.artikelnr,
                        typ: 'antidroehm',
                        beschreibung: fallbackResult.bezeichnung + ' (Fallback)',
                        menge: fallbackResult.anzahl * item.anzahl,
                        einheit: 'Stk',
                        fensterbank_id: item.id,
                        fensterbank_info: fensterbank_info
                      });
                    }
                  }
                }
              }
            } // Ende der for-Schleife
          } catch (error) {
            console.error('❌ Fehler bei Antidröhn-Berechnung:', error);
          }
        } else {
          console.log('⏭️ Antidröhnstreifen übersprungen, item.antidroehm =', item.antidroehm);
        }

        // 5. EPDM Anschlussdichtung - Projektweite Summierung
        if (item.anschlussdichtung === 'ja') {
          // Sammle alle Fertigmaße für projektweite EPDM-Berechnung
          const totalFmForEpdm = warenkorb
            .filter(w => w.anschlussdichtung === 'ja')
            .reduce((sum, w) => sum + (w.fertigmass * w.anzahl), 0);
          
          try {
            console.log('🔍 Berechne EPDM Anschlussdichtung projektweite Summe:', totalFmForEpdm + 'mm');
            
            // Nur einmal für die ganze Projekt-Summe berechnen (beim ersten Item)
            const isFirstEpdmItem = warenkorb.findIndex(w => w.anschlussdichtung === 'ja') === warenkorb.findIndex(w => w.id === item.id);
            
            if (isFirstEpdmItem) {
              const dichtungResponse = await fetch(`${BACKEND_URL}/api/calculate/anschlussdichtung-epdm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  total_fm_mm: totalFmForEpdm
                })
              });
              
              if (dichtungResponse.ok) {
                const dichtungResult = await dichtungResponse.json();
                console.log('✅ EPDM Anschlussdichtung Ergebnis:', dichtungResult);
                if (dichtungResult.anzahl > 0) {
                  console.log('➕ Füge EPDM Anschlussdichtung hinzu:', dichtungResult.artikelnr);
                  addArtikel({
                    artikelnr: dichtungResult.artikelnr,
                    typ: 'anschlussdichtung',
                    beschreibung: `${dichtungResult.bezeichnung} (projektweite Summe: ${(totalFmForEpdm/1000).toFixed(2)}m)`,
                    menge: dichtungResult.anzahl,
                    einheit: dichtungResult.einheit || 'm',
                    fensterbank_id: 'projekt_summe',
                    fensterbank_info: `Projektweite EPDM-Summe: ${(totalFmForEpdm/1000).toFixed(2)}m`
                  });
                } else {
                  console.log('⚠️ EPDM Anschlussdichtung anzahl = 0');
                }
              } else {
                console.error('❌ EPDM Anschlussdichtung API Fehler:', dichtungResponse.status);
                
                // Fallback: Verwende alte API
                const fallbackResponse = await fetch(`${BACKEND_URL}/api/calculate/anschlussdichtung`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    fertigmass_mm: item.fertigmass,
                    anschlussdichtung_aktiv: 'ja'
                  })
                });
                
                if (fallbackResponse.ok) {
                  const fallbackResult = await fallbackResponse.json();
                  if (fallbackResult.anzahl > 0) {
                    addArtikel({
                      artikelnr: fallbackResult.artikelnr,
                      typ: 'anschlussdichtung',
                      beschreibung: fallbackResult.bezeichnung,
                      menge: (fallbackResult.anzahl * item.anzahl).toFixed(2),
                      einheit: fallbackResult.einheit || 'lfm',
                      fensterbank_id: item.id,
                      fensterbank_info: fensterbank_info
                    });
                  }
                }
              }
            } else {
              console.log('⏭️ EPDM bereits für Projekt-Summe berechnet');
            }
          } catch (error) {
            console.error('❌ Fehler bei EPDM-Berechnung:', error);
          }
        } else {
          console.log('⏭️ EPDM Anschlussdichtung übersprungen, item.anschlussdichtung =', item.anschlussdichtung);
        }

        // 6. Stoßverbinder (falls erforderlich)
        if (item.fertigmass > 3000) {
          try {
            const stossResponse = await fetch(`${BACKEND_URL}/api/calculate/stossverbinder`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fertigmass_mm: item.fertigmass,
                oberflaeche_code: oberflaeche,
                bordprofil_code: bordprofilCode
              })
            });
            
            if (stossResponse.ok) {
              const stossResult = await stossResponse.json();
              if (stossResult.stossverbinder_noetig && stossResult.anzahl_stoesse > 0) {
                const stossArtikelNr = `1420${oberflaeche}${item.ausladung.toString().padStart(3, '0')}`;
                addArtikel({
                  artikelnr: stossArtikelNr,
                  typ: 'stossverbinder',
                  beschreibung: `Stoßverbinder für mehrteilige Ausführung`,
                  menge: stossResult.anzahl_stoesse * item.anzahl, // Anzahl Stöße × Anzahl Fensterbänke
                  einheit: 'Stk',
                  fensterbank_id: item.id,
                  fensterbank_info: fensterbank_info
                });
              }
            }
          } catch (error) {
            console.error('Fehler bei Stoßverbinder-Berechnung:', error);
          }
        }
      }

      // Konvertiere Map zu Array und sortiere
      const alleArtikel = Array.from(artikelMap.values()).sort((a, b) => {
        // Sortierung: hauptprodukt -> bordprofil -> fensterbankhalter -> antidroehm -> anschlussdichtung -> stossverbinder
        const order = { 'hauptprodukt': 1, 'bordprofil': 2, 'fensterbankhalter': 3, 'antidroehm': 4, 'anschlussdichtung': 5, 'stossverbinder': 6 };
        return (order[a.typ] || 999) - (order[b.typ] || 999);
      });

      console.log('🏁 ENDE berechneArtikelListe - Finale Artikel-Liste:', alleArtikel.length, 'Artikel');
      console.log('Artikel-Typen:', alleArtikel.map(a => `${a.typ}: ${a.artikelnr}`));
      
      setArtikelListe(alleArtikel);
    } catch (error) {
      console.error('Fehler bei der Artikelberechnung:', error);
    }
  };

  // VPE-System: Artikel-Liste mit echten Backend-Artikelnummern-Generierung
  const [echteArtikelListe, setEchteArtikelListe] = useState([]);

  // Farbcode-Mapping für Artikelnummern (erweitert für alle Varianten)
  const getFarbcode = (farbe) => {
    if (!farbe) return '3'; // Default: RAL9016 weiß
    
    // Umwandlung zu lowercase für robuste Vergleiche
    const farbeLower = farbe.toLowerCase();
    
    // natur-eloxiert (alle Varianten)
    if (farbeLower.includes('natur-eloxiert') || farbeLower.includes('e6/ev1')) return '2';
    
    // RAL9016 weiß
    if (farbeLower.includes('ral9016') || farbeLower.includes('weiß')) return '3';
    
    // RAL7016 anthrazitgrau  
    if (farbeLower.includes('ral7016') || farbeLower.includes('anthrazit')) return '7';
    
    // RAL bunt
    if (farbeLower.includes('ral bunt') || farbeLower.includes('pflichtangabe')) return '6';
    
    // Default fallback
    return '3'; // RAL9016 weiß
  };

  const generiereEchteArtikelListe = () => {
    if (produktListe.length === 0) {
      setEchteArtikelListe([]);
      return;
    }

    const artikelListe = [];
    let position = 1;

    produktListe.forEach(item => {
      if (item.typ === 'Stangenware') {
        // KORRIGIERTE Backend-Logik: 1010 + Farbcode + Ausladung (3-stellig)
        const farbcode = getFarbcode(item.farbe);
        const ausladung_str = String(item.ausladung).padStart(3, '0'); // 050, 165, etc.
        const artikelnr = `1010${farbcode}${ausladung_str}`;

        artikelListe.push({
          position: position++,
          artikelnr: artikelnr,
          beschreibung: `sill|tec40 BASE Alu-Außenfensterbank`,
          menge: item.anzahl_vpe * 10, // VPE * 10 Stäbe
          einheit: 'Stk',
          details: `${item.ausladung}mm ${item.farbe} | ${item.anzahl_vpe} VPE = ${item.anzahl_vpe * 10} Stäbe à 6m`,
          typ: 'hauptprodukt',
          vpe_info: `${item.anzahl_vpe} VPE à 10 Stäbe = ${item.gesamtlaenge_m}m`
        });
      } else if (item.typ === 'Bordprofile') {
        // ERWEITERTE Bordprofile Logik - sowohl mit als auch ohne Pipe-Zeichen
        const bordprofilMap = {
          // Mit Pipe-Zeichen (Backend-Format)
          'AA|100': { links: '1100', rechts: '1101' },
          'AA|150': { links: '1150', rechts: '1151' },
          'GAA|320': { links: '1320', rechts: '1321' },
          'GA|125-P': { links: '1125', rechts: '1126' },
          'GA|125-K': { links: '1127', rechts: '1128' },
          // Ohne Pipe-Zeichen (möglicherweise Frontend-Format)
          'AA100': { links: '1100', rechts: '1101' },
          'AA150': { links: '1150', rechts: '1151' },
          'GAA320': { links: '1320', rechts: '1321' },
          'GA125-P': { links: '1125', rechts: '1126' },
          'GA125-K': { links: '1127', rechts: '1128' },
          // Weitere Varianten ohne Bindestrich
          'GA125P': { links: '1125', rechts: '1126' },
          'GA125K': { links: '1127', rechts: '1128' }
        };
        
        // Debug: Prüfe was item.bordprofil tatsächlich enthält
        console.log('🔍 Bordprofil Debug:', {
          'item.bordprofil': item.bordprofil,
          'typeof': typeof item.bordprofil,
          'verfügbare Keys': Object.keys(bordprofilMap),
          'gefunden': !!bordprofilMap[item.bordprofil]
        });
        
        const profilCodes = bordprofilMap[item.bordprofil];
        if (!profilCodes) {
          console.error(`❌ Bordprofil '${item.bordprofil}' nicht in Map gefunden!`);
          console.error('📋 Verfügbare Keys:', Object.keys(bordprofilMap));
        } else {
          console.log(`✅ Bordprofil '${item.bordprofil}' gefunden:`, profilCodes);
        }
        const finalProfilCodes = profilCodes || { links: '1320', rechts: '1321' }; // Default GAA320
        
        // KORREKT: Farbcode und Ausladung aus dem Bordprofil-Item selbst
        const farbcode = getFarbcode(item.farbe || 'RAL9016 weiß');
        const ausladung_str = String(item.ausladung || 165).padStart(3, '0');
        
        if (item.links_vpe > 0) {
          const artikelnr = `${finalProfilCodes.links}${farbcode}${ausladung_str}`;
          console.log(`🔧 Links Artikelnr: '${item.bordprofil}' -> ${finalProfilCodes.links} + ${farbcode} + ${ausladung_str} = ${artikelnr}`);
          artikelListe.push({
            position: position++,
            artikelnr: artikelnr,
            beschreibung: `Bordprofil ${item.bordprofil} Links`,
            menge: item.links_vpe * 30,
            einheit: 'Stk',
            details: `${item.links_vpe} VPE à 30 Stück | ${item.ausladung}mm ${item.farbe}`,
            typ: 'bordprofil'
          });
        }
        
        if (item.rechts_vpe > 0) {
          const artikelnr = `${finalProfilCodes.rechts}${farbcode}${ausladung_str}`;
          console.log(`🔧 Rechts Artikelnr: '${item.bordprofil}' -> ${finalProfilCodes.rechts} + ${farbcode} + ${ausladung_str} = ${artikelnr}`);
          artikelListe.push({
            position: position++,
            artikelnr: artikelnr,
            beschreibung: `Bordprofil ${item.bordprofil} Rechts`,
            menge: item.rechts_vpe * 30,
            einheit: 'Stk',
            details: `${item.rechts_vpe} VPE à 30 Stück | ${item.ausladung}mm ${item.farbe}`,
            typ: 'bordprofil'
          });
        }
      } else if (item.typ === 'Zubehör') {
        // ECHTE Zubehör Artikelnummern (unverändert)
        let artikelnr = '';
        let einheit = 'Stk';
        let menge = item.anzahl_vpe;
        
        if (item.beschreibung.includes('Anti-Dröhn')) {
          // Anti-Dröhn-Streifen: 50mm=40000015, 80mm=40000016, 100mm=40000017
          const antidröhnMap = {
            '50': '40000015',
            '80': '40000016', 
            '100': '40000017'
          };
          artikelnr = antidröhnMap[item.breite] || '40000016'; // Default 80mm
          einheit = 'm';
          menge = item.anzahl_vpe * 100; // 100m VPE
        } else if (item.beschreibung.includes('Anschlussdichtung')) {
          artikelnr = '40000022'; // Anschlussdichtung EPDM
          einheit = 'm';
          menge = item.anzahl_vpe * 50; // 50m VPE
        } else if (item.beschreibung.includes('Halter|ISO')) {
          // ISO Halter Größe 1-6: 18001016-18001021
          const größeMatch = item.beschreibung.match(/Größe (\d)/);
          const größe = größeMatch ? parseInt(größeMatch[1]) : 1;
          artikelnr = `1800101${5 + größe}`; // Größe 1=18001016, Größe 2=18001017, etc.
          einheit = 'Stk';
          menge = item.anzahl_vpe * 10; // 10 Stück VPE
        } else if (item.beschreibung.includes('Halter|Putz')) {
          artikelnr = '18001001'; // Halter Putz
          einheit = 'Stk';
          menge = item.anzahl_vpe * 50; // 50 Stück VPE
        } else if (item.beschreibung.includes('Halter|Sichtbeton')) {
          artikelnr = '18001002'; // Halter Sichtmauerwerk
          einheit = 'Stk';
          menge = item.anzahl_vpe * 50; // 50 Stück VPE
        }

        if (artikelnr) {
          artikelListe.push({
            position: position++,
            artikelnr: artikelnr,
            beschreibung: item.beschreibung,
            menge: menge,
            einheit: einheit,
            details: item.details,
            typ: 'zubehör'
          });
        }
      }
    });

    setEchteArtikelListe(artikelListe);
  };

  // Automatisch echte Artikel-Liste generieren wenn produktListe sich ändert
  useEffect(() => {
    if (currentStep === 1) { // Nur auf Step 2
      generiereEchteArtikelListe();
    }
  }, [produktListe, currentStep]);

  // Segmentierung für Stoßverbinder State
  const [segmentierung, setSegmentierung] = useState(null);
  
  // State für Artikel-Liste (vollständige Berechnung)
  const [artikelListe, setArtikelListe] = useState([]);

  // Schnittmaß berechnung (SM = FM - SM-Abzug, bei >3000mm mit Segmentierung)
  const calculateSchnittmass = useMemo(() => {
    if (!currentBank.fertigmass || !configData) return '';
    
    const fm = parseInt(currentBank.fertigmass);
    if (isNaN(fm)) return '';
    
    // Advanced Mode: Unterschiedliche Bordprofile
    if (bordprofilAdvanced && bordprofilLinks && bordprofilRechts) {
      const bordprofilLinksData = configData.bordprofile.find(bp => bp.code === bordprofilLinks);
      const bordprofilRechtsData = configData.bordprofile.find(bp => bp.code === bordprofilRechts);
      
      if (!bordprofilLinksData || !bordprofilRechtsData) return fm;
      
      // SM = FM - Abzug_Links - Abzug_Rechts
      const abzugLinks = bordprofilLinksData.sm_abzug || 7.5;  // Default 7.5mm
      const abzugRechts = bordprofilRechtsData.sm_abzug || 7.5;
      const sm = fm - abzugLinks - abzugRechts;
      
      return sm > 0 ? Math.floor(sm + 0.5) : ''; // Rundung mit .5 → aufrunden
    }
    
    // Standard Mode: Gleiches Bordprofil
    if (!currentBank.bordprofil) return '';
    
    const selectedBordprofil = configData.bordprofile.find(bp => bp.code === currentBank.bordprofil);
    if (!selectedBordprofil) return fm;
    
    // Bei Längen über 3000mm: Segmentierung vom Backend holen
    if (fm > 3000) {
      // Verwende Segmentierung wenn verfügbar
      if (segmentierung && segmentierung.segmente_sm && segmentierung.segmente_sm.length > 1) {
        return `${segmentierung.anzahl_segmente} Stück à ${segmentierung.segmente_sm[0]}mm`;
      } else {
        // Fallback: Einfache Berechnung
        const sm = fm - (selectedBordprofil.sm_abzug * 2);
        return sm > 0 ? `~${sm}mm (wird segmentiert)` : '';
      }
    } else {
      // Standard SM-Berechnung für Längen ≤3000mm
      const sm = fm - (selectedBordprofil.sm_abzug * 2);
      return sm > 0 ? sm : '';
    }
  }, [currentBank.fertigmass, currentBank.bordprofil, configData, segmentierung, bordprofilAdvanced, bordprofilLinks, bordprofilRechts]);

  // Segmentierung laden wenn Länge >3000mm
  useEffect(() => {
    const fm = parseInt(currentBank.fertigmass);
    if (fm > 3000 && currentBank.bordprofil) {
      // API-Call für Segmentierung
      const fetchSegmentierung = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/calculate/stossverbinder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fertigmass_mm: fm,
              bordprofil_links: currentBank.bordprofil,
              bordprofil_rechts: currentBank.bordprofil
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            setSegmentierung(data);
          }
        } catch (err) {
          console.error('Segmentierung fetch error:', err);
          setSegmentierung(null);
        }
      };
      
      const timer = setTimeout(fetchSegmentierung, 300); // Debounce
      return () => clearTimeout(timer);
    } else {
      setSegmentierung(null);
    }
  }, [currentBank.fertigmass, currentBank.bordprofil]);

  // Fensterbank zu Warenkorb hinzufügen
  const addToWarenkorb = () => {
    if (!currentBank.fertigmass || (!bordprofilAdvanced && !currentBank.bordprofil) || (bordprofilAdvanced && (!bordprofilLinks || !bordprofilRechts))) {
      alert('Bitte Fertigmaß und Bordprofil auswählen');
      return;
    }

    const newItem = {
      id: Date.now(),
      ...currentBank,
      fertigmass: parseInt(currentBank.fertigmass),
      schnittmass: calculateSchnittmass,
      ralBezeichnung: currentBank.farbe === 'RAL bunt (RAL-Code Pflichtangabe)' ? ralBezeichnung : '',
      // Bordprofil - je nach Modus
      bordprofil: bordprofilAdvanced ? `${bordprofilLinks}|${bordprofilRechts}` : currentBank.bordprofil,
      bordprofilLinks: bordprofilAdvanced ? bordprofilLinks : currentBank.bordprofil,
      bordprofilRechts: bordprofilAdvanced ? bordprofilRechts : currentBank.bordprofil,
      // Zubehör aus State hinzufügen
      antidroehm: zubehoer.antidroehm,
      antidroehm_modus: antidroehn_modus,       // Neuer Modus
      // antidroehm_breite wird automatisch bestimmt
      halter: zubehoer.halter,
      daemmstaerke: zubehoer.daemmstaerke,
      anschlussdichtung: zubehoer.anschlussdichtung,
      butylEcke: zubehoer.butylEcke
    };

    setWarenkorb(prev => [...prev, newItem]);
    
    // Smart Reset: Behalte alle Einstellungen bei, setze nur Länge und Anzahl zurück
    setCurrentBank(prev => ({
      ...prev,  // Behalte alle aktuellen Einstellungen (inkl. RAL Custom)
      fertigmass: '',  // Nur Länge zurücksetzen
      anzahl: 1        // Und Anzahl auf 1 zurücksetzen
    }));

    // Bordprofil Advanced Mode zurücksetzen
    setBordprofilAdvanced(false);
    setBordprofilLinks('GAA320');
    setBordprofilRechts('GAA320');
    
    // Anti-Dröhn Modus NICHT zurücksetzen - User wählt einmal für alle Fensterbänke
    // setAntidroehn_modus('lose'); // ENTFERNT: Modus soll beibehalten werden
  };

  // Artikel aus Warenkorb entfernen
  const removeFromWarenkorb = (id) => {
    setWarenkorb(prev => prev.filter(item => item.id !== id));
  };

  // Bestellung absenden
  const handleSubmitOrder = async () => {
    if (orderSubmitting) return;

    setOrderSubmitting(true);
    setOrderError(null);

    try {
      // Validierung
      const requiredFields = ['unternehmen', 'ansprechpartner', 'email', 'strasse', 'plz', 'ort'];
      const missingFields = requiredFields.filter(field => !kundendaten[field]);

      if (missingFields.length > 0) {
        setOrderError('Bitte füllen Sie alle Pflichtfelder aus: ' + missingFields.join(', '));
        setOrderSubmitting(false);
        return;
      }

      if (produktListe.length === 0) {
        setOrderError('Ihre Produktliste ist leer. Bitte fügen Sie mindestens ein Produkt zur Liste hinzu.');
        setOrderSubmitting(false);
        return;
      }

      // Order payload vorbereiten - VPE LAGERLÄNGEN SYSTEM
      const orderData = {
        kundendaten: kundendaten,
        warenkorb: produktListe, // Verwende produktListe als warenkorb für Backend-Kompatibilität
        artikel_liste: [] // Wird vom Backend generiert
      };

      console.log('Submitting order:', orderData);

      // API-Call
      const response = await fetch(`${BACKEND_URL}/api/submit-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Server Error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Order submission successful:', result);

      // Erfolg: Wechsle zu Step 3 (Bestätigung)
      setOrderResult(result);
      setCurrentStep(3);
      
      // AUTO-RESET: Nach 30 Sekunden zurück zu Step 1
      setTimeout(() => {
        console.log('🔄 Auto-Reset: Zurück zu Step 1 nach 30 Sekunden');
        setCurrentStep(0); // Zurück zu Step 1
        setProduktListe([]); // Liste leeren
        setWarenkorb([]); // Warenkorb leeren
        setOrderResult(null); // Ergebnis zurücksetzen
        setOrderError(''); // Fehler zurücksetzen
        // Auch andere States zurücksetzen
        setCurrentBank({
          ausladung: 165,
          farbe: 'RAL9016 weiß',
          bordprofil: 'GAA|320',
          anzahl_vpe: 1,
          bordprofil_links_vpe: 0,
          bordprofil_rechts_vpe: 0
        });
        setZubehoer({
          antidroehm_breite: 'keine',
          antidroehm_vpe: 0,
          halter: 'keine',
          halter_groesse: '1',
          halter_vpe: 0,
          anschlussdichtung: 'keine',
          anschlussdichtung_vpe: 0
        });
        setKundendaten({
          unternehmen: '',
          ansprechpartner: '',
          email: '',
          telefon: '',
          strasse: '',
          plz: '',
          ort: '',
          kommentar: ''
        });
      }, 30000); // 30 Sekunden

    } catch (error) {
      console.error('Order submission error:', error);
      setOrderError(error.message || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Konfigurator...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <h3 className="text-red-800 font-semibold mb-2">Fehler beim Laden</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-xl font-bold">eurosill</div>
              <div className="text-sm opacity-90">Konfigurator</div>
              <div className="text-xs opacity-75">sillitec40 BASE</div>
            </div>
            <div className="text-sm opacity-90">Lagerbestände auffüllen | Made in Germany</div>
          </div>
        </div>
      </header>

      {/* Stepper - Simple, horizontal like reference */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center space-x-8">
            {[
              { num: 1, label: 'Standard Fensterbank' },
              { num: 2, label: 'Konfiguration prüfen' },
              { num: 3, label: 'Kundendaten' },
              { num: 4, label: 'Bestätigung' }
            ].map((step, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  index < currentStep 
                    ? 'bg-green-600 text-white' 
                    : index === currentStep 
                      ? 'bg-gray-800 text-white' 
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {index < currentStep ? '✓' : step.num}
                </div>
                <span className="text-sm">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        
        {/* Step 0: Konfigurator - Exactly like Reference Images */}
        {currentStep === 0 && (
          <div className="bg-white border border-gray-300">
            {/* Header like reference image */}
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800">Konfigurator</h2>
              </div>
              <div className="flex text-sm text-gray-600 space-x-6 mt-1">
                <span className="underline">Standard Fensterbank</span>
                <span>Konfiguration prüfen</span>
              </div>
            </div>
            
            <div className="p-4 space-y-0">
              {/* Kommission */}
              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Kommission</div>
                <div className="col-span-6">
                  <input
                    type="text"
                    value={kommission}
                    onChange={(e) => setKommission(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-3 flex justify-end">
                </div>
              </div>

              {/* 1. Ausladung */}
              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Ausladung*</div>
                <div className="col-span-6">
                  <select
                    value={currentBank.ausladung}
                    onChange={(e) => setCurrentBank(prev => ({ ...prev, ausladung: parseInt(e.target.value) }))}
                    className="w-48 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {configData?.ausladungen.map(aus => (
                      <option key={aus.wert} value={aus.wert}>{aus.wert}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3"></div>
              </div>

              {/* 2. Farbe */}
              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Farbe*</div>
                <div className="col-span-6">
                  <select
                    value={currentBank.farbe}
                    onChange={(e) => {
                      setCurrentBank(prev => ({ ...prev, farbe: e.target.value, ralCustom: '' }));
                      setRalBezeichnung('');
                    }}
                    className="w-64 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {configData?.farben.filter(farbe => farbe.name !== 'RAL-Sonderfarbe').map(farbe => (
                      <option key={farbe.code} value={farbe.name}>{farbe.name}</option>
                    ))}
                  </select>
                  
                  {currentBank.farbe === 'RAL bunt (RAL-Code Pflichtangabe)' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={currentBank.ralCustom}
                        onChange={(e) => setCurrentBank(prev => ({ 
                          ...prev, 
                          ralCustom: e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                        }))}
                        placeholder="RAL-Nummer (z.B. 7016)"
                        className="w-48 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      {ralBezeichnung && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ {ralBezeichnung}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-3"></div>
              </div>

              {/* 3. VPE Anzahl - Überschrift oben */}
              <div className="py-2 border-b border-gray-200">
                <div className="mb-3">
                  <div className="text-sm text-gray-700 font-bold">
                    Wählen Sie die VPE-Anzahl der Stangenware aus:
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3 text-sm">VPE Anzahl*</div>
                  <div className="col-span-6">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={currentBank.anzahl_vpe}
                        onChange={(e) => setCurrentBank(prev => ({ 
                          ...prev, 
                          anzahl_vpe: parseInt(e.target.value) || 1
                        }))}
                        className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      <span className="text-sm">VPE à 10 Stäbe (6m)</span>
                    </div>
                    {/* VPE-Info */}
                    {currentBank.anzahl_vpe > 0 && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                        <strong>📦 Bestellung:</strong><br/>
                        {currentBank.anzahl_vpe} VPE = {currentBank.anzahl_vpe * 10} Stäbe = {currentBank.anzahl_vpe * 60}m Gesamtlänge
                      </div>
                    )}
                  </div>
                  <div className="col-span-3"></div>
                </div>
              </div>

              {/* Stangenware zur Liste hinzufügen */}
              <div className="py-3 border-b border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => handleAddStangenware()}
                    className="bg-[#FF6B00] hover:bg-[#e55a00] text-white px-6 py-2 rounded text-sm font-medium"
                  >
                    ✅ Stangenware zur Liste hinzufügen
                  </button>
                </div>
              </div>
              <div className="py-4 border-b border-gray-200">
                <div className="mb-3">
                  <div className="text-sm text-gray-700 font-bold">
                    Wählen Sie die Bordprofile und die dazugehöhrige VPE-Anzahl aus:
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3 text-sm">Bordprofile*</div>
                  <div className="col-span-6">
                    
                    {/* Bordprofil-Auswahl (Bilder) */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {configData?.bordprofile.filter(bp => bp.code !== 'AA175' && bp.code !== 'GAA350').map(bordprofil => (
                        <label key={bordprofil.code} className="block cursor-pointer">
                          <input
                            type="radio"
                            name="seitenabschluss"
                            checked={currentBank.bordprofil === bordprofil.code}
                            onChange={() => setCurrentBank(prev => ({ ...prev, bordprofil: bordprofil.code }))}
                            className="sr-only"
                          />
                          <div className={`border-2 p-4 text-center transition-all ${
                            currentBank.bordprofil === bordprofil.code 
                              ? 'border-orange-500 bg-orange-50' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}>
                            <div className="w-full h-16 mb-3 flex items-center justify-center bg-white p-2">
                              {bordprofil.code === 'AA100' ? (
                                <img 
                                  src="https://customer-assets.emergentagent.com/job_windowsill-maker/artifacts/uiwfcv36_AA100.jpg" 
                                  alt={bordprofil.bezeichnung}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : bordprofil.code === 'AA150' ? (
                                <img 
                                  src="https://customer-assets.emergentagent.com/job_windowsill-maker/artifacts/evivxy6k_AA150.jpg" 
                                  alt={bordprofil.bezeichnung}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : bordprofil.code === 'GAA320' ? (
                                <img 
                                  src="https://customer-assets.emergentagent.com/job_windowsill-maker/artifacts/mqdwxclk_GAA320.jpg" 
                                  alt={bordprofil.bezeichnung}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : bordprofil.code === 'GA125P' ? (
                                <img src="https://customer-assets.emergentagent.com/job_sill-konfigurator/artifacts/l1z6bqkm_AA-125P%20ohne%20Hintergrund%20orthogonal.png" 
                                  alt={bordprofil.bezeichnung}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : bordprofil.code === 'GA125K' ? (
                                <img src="https://customer-assets.emergentagent.com/job_sill-konfigurator/artifacts/hm1fv4st_AA-125K%20ohne%20Hintergrund%20orthogonal.png"
                                  alt={bordprofil.bezeichnung}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                  {bordprofil.bezeichnung}
                                </div>
                              )}
                            </div>
                            <div className="text-xs font-medium">{bordprofil.bezeichnung}</div>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Bordprofil VPE-Erfassung Links/Rechts */}
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded">
                      <h6 className="text-sm font-medium mb-3 text-gray-700">VPE-Bestellung (30 Stück pro VPE):</h6>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Links VPE:</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={currentBank.bordprofil_links_vpe || 0}
                              onChange={(e) => setCurrentBank(prev => ({ 
                                ...prev, 
                                bordprofil_links_vpe: parseInt(e.target.value) || 0
                              }))}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <span className="text-sm text-gray-600">VPE</span>
                          </div>
                          {currentBank.bordprofil_links_vpe > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                              = {currentBank.bordprofil_links_vpe * 30} Stück
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rechts VPE:</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={currentBank.bordprofil_rechts_vpe || 0}
                              onChange={(e) => setCurrentBank(prev => ({ 
                                ...prev, 
                                bordprofil_rechts_vpe: parseInt(e.target.value) || 0
                              }))}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <span className="text-sm text-gray-600">VPE</span>
                          </div>
                          {currentBank.bordprofil_rechts_vpe > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                              = {currentBank.bordprofil_rechts_vpe * 30} Stück
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Gesamt-Info */}
                      {(currentBank.bordprofil_links_vpe > 0 || currentBank.bordprofil_rechts_vpe > 0) && (
                        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                          <strong>📦 Bordprofil-Bestellung:</strong><br/>
                          Gesamt: {(currentBank.bordprofil_links_vpe || 0) + (currentBank.bordprofil_rechts_vpe || 0)} VPE = {((currentBank.bordprofil_links_vpe || 0) + (currentBank.bordprofil_rechts_vpe || 0)) * 30} Stück
                        </div>
                      )}
                    </div>
                    
                  </div>
                  <div className="col-span-3"></div>
                </div>
                
                {/* Bordprofile zur Liste hinzufügen */}
                <div className="flex justify-end mt-3">
                  <button
                    onClick={() => handleAddBordprofile()}
                    className="bg-[#FF6B00] hover:bg-[#e55a00] text-white px-6 py-2 rounded text-sm font-medium"
                  >
                    ✅ Bordprofile zur Liste hinzufügen
                  </button>
                </div>
              </div>

              {/* Weitere Konfiguration */}

              {/* Zubehör Sektion - Überschrift oben */}
              <div className="py-2 border-b border-gray-200">
                <div className="mb-3">
                  <div className="text-sm text-gray-700 font-bold">
                    Wählen Sie weiteres Zubehör aus:
                  </div>
                </div>
              </div>

              {/* Weitere Zeilen wie im Referenzbild */}
              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Antidröhnstreifen*</div>
                <div className="col-span-6 flex gap-2 items-center">
                  <select
                    value={zubehoer.antidroehm_breite || 'keine'}
                    onChange={(e) => {
                      const breite = e.target.value;
                      setZubehoer(prev => ({ 
                        ...prev, 
                        antidroehm: breite === 'keine' ? 'nein' : 'ja',
                        antidroehm_breite: breite
                      }));
                    }}
                    className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="keine">keine</option>
                    <option value="50">50mm</option>
                    <option value="80">80mm</option>
                    <option value="100">100mm</option>
                  </select>
                  
                  {zubehoer.antidroehm_breite && zubehoer.antidroehm_breite !== 'keine' && (
                    <>
                      {/* VPE-Eingabe für Anti-Dröhn */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={zubehoer.antidroehm_vpe || 0}
                          onChange={(e) => setZubehoer(prev => ({ 
                            ...prev, 
                            antidroehm_vpe: parseInt(e.target.value) || 0
                          }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">VPE</span>
                      </div>
                      <span className="text-xs text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded">
                        VPE = 100m lose
                      </span>
                      {zubehoer.antidroehm_vpe > 0 && (
                        <div className="text-xs text-green-600">
                          = {zubehoer.antidroehm_vpe * 100}m
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="col-span-3"></div>
              </div>

              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Fensterbankhalter*</div>
                <div className="col-span-6">
                  <div className="flex gap-2 items-center flex-wrap">
                    <select
                      value={zubehoer.halter}
                      onChange={(e) => {
                        setZubehoer(prev => ({ ...prev, halter: e.target.value, daemmstaerke: '', halter_groesse: '' }));
                      }}
                      className="w-64 border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="keine">keine</option>
                      <option value="Halter|ISO">Halter|ISO</option>
                      <option value="Halter|Putz">Halter|Putz</option>
                      <option value="Halter|Sichtbeton">Halter|Sichtbeton</option>
                    </select>
                    
                    {/* ISO Größen-Auswahl */}
                    {zubehoer.halter === 'Halter|ISO' && (
                      <div className="flex gap-2 items-center">
                        <select
                          value={zubehoer.halter_groesse || ''}
                          onChange={(e) => setZubehoer(prev => ({ ...prev, halter_groesse: e.target.value }))}
                          className="w-40 border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="">Größe wählen</option>
                          <option value="1">Größe 1: 90-150mm</option>
                          <option value="2">Größe 2: 120-180mm</option>
                          <option value="3">Größe 3: 160-220mm</option>
                          <option value="4">Größe 4: 220-280mm</option>
                          <option value="5">Größe 5: 240-300mm</option>
                          <option value="6">Größe 6: 280-340mm</option>
                        </select>
                        
                        {/* VPE-Eingabe für ISO Halter */}
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={zubehoer.halter_vpe || 0}
                            onChange={(e) => setZubehoer(prev => ({ 
                              ...prev, 
                              halter_vpe: parseInt(e.target.value) || 0
                            }))}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <span className="text-sm text-gray-600">VPE</span>
                        </div>
                        <span className="text-xs text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded">
                          VPE = 10 Stück
                        </span>
                        {zubehoer.halter_vpe > 0 && (
                          <div className="text-xs text-green-600">
                            = {zubehoer.halter_vpe * 10} Stück
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* VPE für andere Halter-Typen */}
                    {(zubehoer.halter === 'Halter|Putz' || zubehoer.halter === 'Halter|Sichtbeton') && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={zubehoer.halter_vpe || 0}
                          onChange={(e) => setZubehoer(prev => ({ 
                            ...prev, 
                            halter_vpe: parseInt(e.target.value) || 0
                          }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">VPE</span>
                        <span className="text-xs text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded">
                          VPE = 50 Stück
                        </span>
                        {zubehoer.halter_vpe > 0 && (
                          <div className="text-xs text-green-600">
                            = {zubehoer.halter_vpe * 50} Stück
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Dämmstärke-Info nur für alte Referenz - entfernt da jetzt über Größe gewählt */}
                </div>
                <div className="col-span-3"></div>
              </div>

              <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                <div className="col-span-3 text-sm">Anschlussdichtung*</div>
                <div className="col-span-6 flex gap-2 items-center">
                  <select
                    value={zubehoer.anschlussdichtung}
                    onChange={(e) => setZubehoer(prev => ({ ...prev, anschlussdichtung: e.target.value }))}
                    className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="keine">keine</option>
                    <option value="ja">ja</option>
                  </select>
                  
                  {zubehoer.anschlussdichtung === 'ja' && (
                    <>
                      {/* VPE-Eingabe für Anschlussdichtung */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={zubehoer.anschlussdichtung_vpe || 0}
                          onChange={(e) => setZubehoer(prev => ({ 
                            ...prev, 
                            anschlussdichtung_vpe: parseInt(e.target.value) || 0
                          }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">VPE</span>
                      </div>
                      <span className="text-xs text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded">
                        VPE = 50m
                      </span>
                      {zubehoer.anschlussdichtung_vpe > 0 && (
                        <div className="text-xs text-green-600">
                          = {zubehoer.anschlussdichtung_vpe * 50}m
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="col-span-3"></div>
              </div>

              {/* Butyl-Ecke nur bei AA|100 oder AA|150 */}
              {(currentBank.bordprofil === 'AA100' || currentBank.bordprofil === 'AA150') && (
                <div className="grid grid-cols-12 gap-4 items-center py-2 border-b border-gray-200">
                  <div className="col-span-3 text-sm">Butyl-Ecke*</div>
                  <div className="col-span-6">
                    <select
                      value={zubehoer.butylEcke}
                      onChange={(e) => setZubehoer(prev => ({ ...prev, butylEcke: e.target.value }))}
                      className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="nein">nein</option>
                      <option value="ja">ja</option>
                    </select>
                  </div>
                  <div className="col-span-3"></div>
                </div>
              )}

              <div className="text-xs text-gray-500 mt-2">* Pflichtfelder</div>

              {/* Zubehör zur Liste hinzufügen */}
              <div className="py-3 border-b border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => handleAddZubehör()}
                    className="bg-[#FF6B00] hover:bg-[#e55a00] text-white px-6 py-2 rounded text-sm font-medium"
                  >
                    ✅ Zubehör zur Liste hinzufügen
                  </button>
                </div>
              </div>

              {/* Kommentar - unabhängig von VPE-Bereichen */}
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-3 text-sm font-medium">Kommentar</div>
                  <div className="col-span-9">
                    <textarea
                      rows="3"
                      placeholder="Hier können Sie zusätzliche Informationen oder Anmerkungen eingeben..."
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                    <div className="text-xs text-gray-500 mt-1">Optional: Zusätzliche Hinweise zur Bestellung</div>
                  </div>
                </div>
              </div>

              {/* Produktliste - VPE-System */}
              {produktListe.length > 0 && (
                <div className="mt-8 border-t pt-6">
                  <h3 className="text-xl font-bold mb-4 text-[#FF6B00]">📦 Bestellliste (VPE-System)</h3>
                  
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#FF6B00] text-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Typ</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Beschreibung</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Details</th>
                          <th className="px-4 py-3 text-center text-sm font-medium">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {produktListe.map((item, index) => (
                          <tr key={item.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                item.typ === 'Stangenware' ? 'bg-green-100 text-green-800' :
                                item.typ === 'Bordprofile' ? 'bg-blue-100 text-blue-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {item.typ}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-sm">{item.beschreibung}</div>
                              {(item.ausladung || item.farbe) && (
                                <div className="text-xs text-gray-600">
                                  {item.ausladung && `Ausladung: ${item.ausladung}mm`}
                                  {item.ausladung && item.farbe && ' | '}
                                  {item.farbe && `Farbe: ${item.farbe}`}
                                  {item.ralCustom && ` RAL${item.ralCustom}`}
                                </div>
                              )}
                              {item.bordprofil && (
                                <div className="text-xs text-gray-600">Profil: {item.bordprofil}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">{item.details}</div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => {
                                  setProduktListe(prev => prev.filter(p => p.id !== item.id));
                                  // SYNCHRONISATION: Auch aus warenkorb entfernen
                                  setWarenkorb(prev => prev.filter(p => p.id !== item.id));
                                }}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                🗑️ Entfernen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm font-medium text-green-800">
                          📊 Gesamt: {produktListe.length} Position(en) in der Bestellliste
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setProduktListe([]);
                            // SYNCHRONISATION: Auch warenkorb leeren
                            setWarenkorb([]);
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm"
                        >
                          🗑️ Liste leeren
                        </button>
                        <button
                          onClick={handleBestellungAbschließen}
                          className="bg-[#FF6B00] hover:bg-[#e55a00] text-white px-4 py-2 rounded text-sm font-medium"
                        >
                          📋 Bestellung abschließen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* LAGERLÄNGEN-SYSTEM: Verwende nur die neue Bestellliste, nicht den alten Warenkorb */}
              {/* Alter Warenkorb für Lagerlängen deaktiviert */}
            </div>
          </div>
        )}

        {/* Step 2: Konfiguration prüfen - VPE LAGERLÄNGEN SYSTEM */}
        {currentStep === 1 && (
          <div className="bg-white border border-gray-300">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
              <h2 className="text-lg font-bold text-gray-800">Konfiguration prüfen</h2>
              <p className="text-sm text-gray-600 mt-1">Bitte überprüfen Sie abschließend Ihre Konfiguration</p>
            </div>
            
            <div className="p-6">
              {/* Kommission */}
              {kommission && (
                <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded">
                  <h3 className="font-semibold text-orange-800 mb-2">Projekt-Information</h3>
                  <p><strong>Kommission:</strong> {kommission}</p>
                </div>
              )}

              {/* Erfasste Fensterbänke - NEUE VPE LAGERLÄNGEN VERSION */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-4">Erfasste Fensterbänke ({produktListe.length} Artikel)</h3>
                
                {/* VPE-LAGERLÄNGEN PRODUKTLISTE ANZEIGE */}
                {produktListe.map((item, index) => (
                  <div key={item.id} className="border border-gray-300 rounded-lg p-6 mb-4">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Linke Spalte - Artikel-Info */}
                      <div>
                        <div className="flex items-center mb-4">
                          <div>
                            <h4 className="font-semibold text-lg">sill|tec40 BASE</h4>
                            <p className="text-sm text-gray-600">{item.beschreibung}</p>
                            <p className="text-sm text-orange-600 font-medium">Artikel #{index + 1}</p>
                          </div>
                        </div>

                        {/* Grunddaten - VPE-SYSTEM */}
                        <div className="space-y-2 text-sm">
                          {item.typ === 'Stangenware' && (
                            <>
                              <div className="flex justify-between">
                                <span className="font-medium">Ausladung:</span>
                                <span>{item.ausladung} mm</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Farbe:</span>
                                <span>{item.farbe === 'RAL bunt (RAL-Code Pflichtangabe)' ? 
                                  `RAL${item.ralCustom} ${item.ralBezeichnung || 'bunt'}` : 
                                  item.farbe}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">VPE Anzahl:</span>
                                <span className="font-bold text-orange-600">{item.anzahl_vpe} VPE</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Stäbe gesamt:</span>
                                <span>{item.anzahl_stäbe} Stäbe</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Gesamtlänge:</span>
                                <span>{item.gesamtlaenge_m}m</span>
                              </div>
                            </>
                          )}
                          
                          {item.typ === 'Bordprofile' && (
                            <>
                              <div className="flex justify-between">
                                <span className="font-medium">Profil:</span>
                                <span>{item.bordprofil}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Links VPE:</span>
                                <span>{item.links_vpe} VPE</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Rechts VPE:</span>
                                <span>{item.rechts_vpe} VPE</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Gesamt VPE:</span>
                                <span className="font-bold text-orange-600">{item.gesamt_vpe} VPE</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Stück gesamt:</span>
                                <span>{item.gesamt_stück} Stück</span>
                              </div>
                            </>
                          )}
                          
                          {item.typ === 'Zubehör' && (
                            <>
                              <div className="flex justify-between">
                                <span className="font-medium">Artikel:</span>
                                <span>{item.beschreibung}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">VPE Anzahl:</span>
                                <span className="font-bold text-orange-600">{item.anzahl_vpe} VPE</span>
                              </div>
                              {item.gesamt_meter && (
                                <div className="flex justify-between">
                                  <span className="font-medium">Meter gesamt:</span>
                                  <span>{item.gesamt_meter}m</span>
                                </div>
                              )}
                              {item.gesamt_stück && (
                                <div className="flex justify-between">
                                  <span className="font-medium">Stück gesamt:</span>
                                  <span>{item.gesamt_stück} Stück</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Rechte Spalte - Zubehör & Optionen */}
                      <div className="bg-gray-50 p-4 rounded">
                        <h5 className="font-semibold mb-3 text-gray-800">Details & Spezifikationen</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Typ:</span>
                            <span className="font-medium text-blue-600">{item.typ}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>VPE-Details:</span>
                            <span className="text-gray-700">{item.details}</span>
                          </div>
                          {item.breite && (
                            <div className="flex justify-between">
                              <span>Breite:</span>
                              <span className="text-orange-600">{item.breite}mm</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Zusammenfassung - VPE LAGERLÄNGEN */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-orange-800 mb-2">Zusammenfassung</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Artikel gesamt:</span>
                    <div className="text-lg font-bold text-orange-600">{produktListe.length}</div>
                  </div>
                  <div>
                    <span className="font-medium">VPE gesamt:</span>
                    <div className="text-lg font-bold text-orange-600">
                      {produktListe.reduce((sum, item) => sum + (item.anzahl_vpe || 0), 0)}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Typ-Verteilung:</span>
                    <div className="text-sm text-orange-600">
                      {produktListe.filter(item => item.typ === 'Stangenware').length} Stangenware<br/>
                      {produktListe.filter(item => item.typ === 'Bordprofile').length} Bordprofile<br/>
                      {produktListe.filter(item => item.typ === 'Zubehör').length} Zubehör
                    </div>
                  </div>
                </div>
              </div>

              {/* VPE ARTIKEL-LISTE - BACKEND-KONFORME ARTIKELNUMMERN */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-orange-800 mb-4">Detaillierte Artikel-Liste (Backend-konforme Nummern)</h3>
                
                {echteArtikelListe.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-orange-100">
                            <th className="border border-orange-300 px-3 py-2 text-left">Pos.</th>
                            <th className="border border-orange-300 px-3 py-2 text-left">Artikelnr.</th>
                            <th className="border border-orange-300 px-3 py-2 text-left">Beschreibung</th>
                            <th className="border border-orange-300 px-3 py-2 text-center">Menge</th>
                            <th className="border border-orange-300 px-3 py-2 text-left">Einheit</th>
                            <th className="border border-orange-300 px-3 py-2 text-left">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {echteArtikelListe.map((artikel, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-orange-25'}>
                              <td className="border border-orange-200 px-3 py-2 text-center font-medium">
                                {artikel.position}
                              </td>
                              <td className="border border-orange-200 px-3 py-2 font-mono text-xs font-bold text-blue-600">
                                {artikel.artikelnr}
                              </td>
                              <td className="border border-orange-200 px-3 py-2">
                                <div className="font-medium">{artikel.beschreibung}</div>
                                {artikel.vpe_info && (
                                  <div className="text-xs text-gray-600">{artikel.vpe_info}</div>
                                )}
                              </td>
                              <td className="border border-orange-200 px-3 py-2 text-center font-bold text-orange-600">
                                {artikel.menge}
                              </td>
                              <td className="border border-orange-200 px-3 py-2">
                                {artikel.einheit}
                              </td>
                              <td className="border border-orange-200 px-3 py-2 text-xs text-gray-700">
                                {artikel.details}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Artikel-Zusammenfassung */}
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-orange-800">Artikel gesamt:</div>
                          <div className="text-xl font-bold text-orange-600">{echteArtikelListe.length}</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-orange-800">Stückzahl gesamt:</div>
                          <div className="text-xl font-bold text-orange-600">
                            {echteArtikelListe.reduce((sum, artikel) => sum + (parseFloat(artikel.menge) || 0), 0)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-orange-800">Generierung:</div>
                          <div className="text-green-600 font-medium">✅ Backend-konforme Logik</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : produktListe.length > 0 ? (
                  <div className="text-center py-8">
                    <div className="text-orange-400 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="font-medium text-orange-600 mb-2">Artikel-Liste wird generiert...</h4>
                    <p className="text-orange-600 text-sm">
                      Die Artikelnummern werden basierend auf der Backend-Logik generiert.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="font-medium text-gray-600 mb-2">Keine Artikel in der Liste</h4>
                    <p className="text-gray-500 text-sm mb-4">
                      Fügen Sie zunächst Produkte zur Liste hinzu, um die detaillierte Artikel-Liste mit echten Artikelnummern zu sehen.
                    </p>
                    <button
                      onClick={() => setCurrentStep(0)}
                      className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 transition-colors"
                    >
                      ← Zurück zur Produkterfassung
                    </button>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(0)}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300 transition-colors"
                >
                  ← Zurück zur Erfassung
                </button>
                <button
                  onClick={() => setCurrentStep(2)}
                  className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700 transition-colors"
                >
                  Weiter zur Bestellung →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Kundendaten-Erfassung */}
        {currentStep === 2 && (
          <div className="bg-white border border-gray-300">
            {/* Header */}
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800">Kundendaten</h2>
              </div>
              <div className="flex text-sm text-gray-600 space-x-6 mt-1">
                <span className="text-gray-400">Standard Fensterbank</span>
                <span className="text-gray-400">Konfiguration prüfen</span>
                <span className="underline text-orange-600 font-medium">Kundendaten</span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Linke Spalte - Unternehmensdaten */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Unternehmensdaten</h3>
                    
                    {/* Unternehmen */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unternehmen <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kundendaten.unternehmen}
                        onChange={(e) => setKundendaten(prev => ({ ...prev, unternehmen: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Firmenname"
                        autoComplete="organization"
                      />
                    </div>

                    {/* Ansprechpartner */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ansprechpartner <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kundendaten.ansprechpartner}
                        onChange={(e) => setKundendaten(prev => ({ ...prev, ansprechpartner: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Vor- und Nachname"
                        autoComplete="name"
                      />
                    </div>

                    {/* E-Mail */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-Mail-Adresse <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={kundendaten.email}
                        onChange={(e) => setKundendaten(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="beispiel@firma.de"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Rechte Spalte - Adressdaten */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Adressdaten</h3>
                    
                    {/* Straße */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Straße <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={kundendaten.strasse}
                        onChange={(e) => setKundendaten(prev => ({ ...prev, strasse: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Straße und Hausnummer"
                        autoComplete="street-address"
                      />
                    </div>

                    {/* PLZ und Ort */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          PLZ <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kundendaten.plz}
                          onChange={(e) => setKundendaten(prev => ({ ...prev, plz: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="12345"
                          maxLength="5"
                          autoComplete="postal-code"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ort <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={kundendaten.ort}
                          onChange={(e) => setKundendaten(prev => ({ ...prev, ort: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="Musterstadt"
                          autoComplete="address-level2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bisheriger Händler (optional) */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="haendler_checkbox"
                      checked={kundendaten.haendler_optional}
                      onChange={(e) => setKundendaten(prev => ({ 
                        ...prev, 
                        haendler_optional: e.target.checked,
                        haendler_daten: e.target.checked ? prev.haendler_daten : ''
                      }))}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="haendler_checkbox" className="ml-2 text-sm font-medium text-gray-700">
                      Bisheriger Händler (optional)
                    </label>
                  </div>
                  
                  {kundendaten.haendler_optional && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Händlerdaten
                      </label>
                      <textarea
                        value={kundendaten.haendler_daten}
                        onChange={(e) => setKundendaten(prev => ({ ...prev, haendler_daten: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        rows="3"
                        placeholder="Name, Adresse, PLZ Ort des bisherigen Händlers..."
                      />
                    </div>
                  )}
                </div>

                {/* Buttons */}
                {/* Fehlerbehandlung */}
                {orderError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <div>
                        <h3 className="text-red-800 font-medium">Fehler beim Versenden der Bestellung</h3>
                        <p className="text-red-700 text-sm mt-1">{orderError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300 transition-colors"
                  >
                    ← Zurück zur Konfiguration
                  </button>
                  <button 
                    onClick={handleSubmitOrder}
                    disabled={orderSubmitting}
                    className={`px-6 py-2 rounded transition-colors ${
                      orderSubmitting 
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    {orderSubmitting ? 'Wird gesendet...' : 'Bestellung absenden →'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Versandbestätigung */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto text-center">
              {/* Erfolgs-Icon */}
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>

              {/* Erfolgsmeldung */}
              <h2 className="text-2xl font-bold text-green-600 mb-4">
                Bestellung erfolgreich übermittelt!
              </h2>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <p className="text-green-800 mb-2">
                  <span className="font-semibold">Bestellnummer:</span> {orderResult?.order_number}
                </p>
                <p className="text-green-700 text-sm">
                  Ihre Bestellung wurde erfolgreich an unser Verkaufsteam weitergeleitet und wird zeitnah bearbeitet.
                </p>
              </div>

              {/* Information Box */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-orange-800 mb-3">Nächste Schritte:</h3>
                <ul className="text-orange-700 text-sm space-y-2 text-left">
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Sie erhalten eine Bestätigungs-E-Mail mit allen Details Ihrer Bestellung
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Unser Verkaufsteam wird binnen 48 Stunden Ihre individuelle Auftragsbestätigung erstellen
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Die detaillierte Auftragsbestätigung erhalten Sie per E-Mail
                  </li>
                  <li className="flex items-start">
                    <span className="text-orange-500 mr-2">•</span>
                    Bei Rückfragen kontaktieren Sie uns unter sales@eurosill.com
                  </li>
                </ul>
              </div>

              {/* Status Informationen */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">E-Mail an Kunde:</span>
                    <span className={`ml-2 ${orderResult?.customer_email_sent ? 'text-green-600' : 'text-red-600'}`}>
                      {orderResult?.customer_email_sent ? '✓ Versendet' : '✗ Fehler'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Interne Benachrichtigung:</span>
                    <span className={`ml-2 ${orderResult?.internal_email_sent ? 'text-green-600' : 'text-red-600'}`}>
                      {orderResult?.internal_email_sent ? '✓ Versendet' : '✗ Fehler'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    // Reset komplett für neue Bestellung
                    setCurrentStep(0);
                    setWarenkorb([]);
                    setKundendaten({
                      unternehmen: '',
                      ansprechpartner: '',
                      email: '',
                      strasse: '',
                      plz: '',
                      ort: '',
                      haendler_optional: false,
                      haendler_daten: ''
                    });
                    setOrderResult(null);
                    setOrderError(null);
                  }}
                  className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Neue Bestellung erstellen
                </button>
                <p className="text-gray-500 text-sm">
                  Vielen Dank für Ihr Vertrauen in eurosill Fensterbänke!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}